export interface HidTrafficDirectoryHandle {
  name: string
  getFileHandle(name: string, options: { create: true }): Promise<{
    createWritable(): Promise<{
      write(data: string): Promise<void>
      close(): Promise<void>
    }>
  }>
}

export interface HidTrafficRecorderStatus {
  state: 'idle' | 'recording' | 'stopping' | 'error'
  fileName: string
  error: string
}

interface TrafficMetadata {
  feature?: boolean
  haveResult?: boolean
  queueDepth?: number
  timeoutMs?: number
  sendDurationMs?: number
  responseDurationMs?: number
  requestCommand?: number
  error?: string
}

interface WritableLogFile {
  write(data: string): Promise<void>
  close(): Promise<void>
}

const FLUSH_INTERVAL_MS = 1000
const FLUSH_SIZE_BYTES = 256 * 1024

const formatTimestampForFile = (date: Date) =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}-${String(date.getHours()).padStart(2, '0')}${String(
    date.getMinutes(),
  ).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`

const bytesToHex = (data: number[]) => data.map((byte) => (byte & 0xff).toString(16).padStart(2, '0')).join(' ')

class HidTrafficRecorder {
  private writable: WritableLogFile | null = null
  private fileName = ''
  private sessionStartedAt = 0
  private sequence = 0
  private pendingLines: string[] = []
  private pendingBytes = 0
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private writeChain: Promise<void> = Promise.resolve()
  private activeDeviceId: number | null = null
  private status: HidTrafficRecorderStatus = { state: 'idle', fileName: '', error: '' }
  private statusListeners = new Set<(status: HidTrafficRecorderStatus) => void>()

  getStatus(): HidTrafficRecorderStatus {
    return { ...this.status }
  }

  subscribe(listener: (status: HidTrafficRecorderStatus) => void) {
    this.statusListeners.add(listener)
    listener(this.getStatus())
    return () => this.statusListeners.delete(listener)
  }

  private setStatus(status: HidTrafficRecorderStatus) {
    this.status = status
    this.statusListeners.forEach((listener) => listener(this.getStatus()))
  }

  async start(deviceId: number, directory: HidTrafficDirectoryHandle): Promise<string> {
    if (this.writable != null) {
      await this.stop('recording_restarted')
    }

    const now = new Date()
    const fileName = `MG75V2-HID-${formatTimestampForFile(now)}.ndjson`
    const fileHandle = await directory.getFileHandle(fileName, { create: true })
    this.writable = await fileHandle.createWritable()
    this.fileName = `${directory.name}/${fileName}`
    this.activeDeviceId = deviceId
    this.sessionStartedAt = performance.now()
    this.sequence = 0
    this.pendingLines = []
    this.pendingBytes = 0
    this.writeChain = Promise.resolve()
    this.setStatus({ state: 'recording', fileName: this.fileName, error: '' })
    this.enqueue({
      event: 'session_start',
      timestamp: now.toISOString(),
      deviceId: `0x${Number(deviceId).toString(16).padStart(8, '0')}`,
      fileName: this.fileName,
      userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    })
    await this.flush()
    return this.fileName
  }

  private enqueue(entry: Record<string, unknown>) {
    const line = `${JSON.stringify(entry)}\n`
    this.pendingLines.push(line)
    this.pendingBytes += line.length
    if (this.pendingBytes >= FLUSH_SIZE_BYTES) {
      void this.flush()
      return
    }
    if (this.flushTimer == null) {
      this.flushTimer = setTimeout(() => void this.flush(), FLUSH_INTERVAL_MS)
    }
  }

  private flush(): Promise<void> {
    if (this.flushTimer != null) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
    if (this.writable == null || this.pendingLines.length === 0) return this.writeChain

    const output = this.pendingLines.join('')
    const writable = this.writable
    this.pendingLines = []
    this.pendingBytes = 0
    this.writeChain = this.writeChain
      .then(() => writable.write(output))
      .catch((error) => {
        const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
        this.setStatus({ state: 'error', fileName: this.fileName, error: message })
        console.error(`Failed to write MG75V2 HID traffic to ${this.fileName}:`, error)
      })
    return this.writeChain
  }

  record(
    deviceId: number,
    event: 'tx' | 'tx_complete' | 'tx_error' | 'rx' | 'response_resolved' | 'response_error',
    data: number[],
    metadata: TrafficMetadata = {},
  ) {
    if (deviceId !== this.activeDeviceId || this.status.state !== 'recording' || this.writable == null) return
    this.enqueue({
      seq: ++this.sequence,
      event,
      timestamp: new Date().toISOString(),
      elapsedMs: Number((performance.now() - this.sessionStartedAt).toFixed(3)),
      length: data.length,
      hex: bytesToHex(data),
      ...metadata,
    })
  }

  async stop(reason: string = 'user_stopped') {
    if (this.writable == null) return
    this.setStatus({ state: 'stopping', fileName: this.fileName, error: '' })
    this.enqueue({
      seq: ++this.sequence,
      event: 'session_end',
      timestamp: new Date().toISOString(),
      elapsedMs: Number((performance.now() - this.sessionStartedAt).toFixed(3)),
      reason,
    })
    await this.flush()

    const writable = this.writable
    this.writable = null
    try {
      await writable.close()
      this.setStatus({ state: 'idle', fileName: this.fileName, error: '' })
    } catch (error) {
      const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
      this.setStatus({ state: 'error', fileName: this.fileName, error: message })
      throw error
    } finally {
      this.sessionStartedAt = 0
      this.sequence = 0
      this.activeDeviceId = null
    }
  }

  finish(reason: string) {
    void this.stop(reason).catch((error) => {
      console.error('Failed to close MG75V2 HID traffic recording:', error)
    })
  }
}

export const hidTrafficRecorder = new HidTrafficRecorder()
